/**
 * Contacts mention provider — registers with the mentions registry so that
 * typing `@` in todo inputs autocompletes against contacts and chips in
 * display mode open the existing contact detail modal.
 */

import React from 'react';
import { registerMentionProvider } from '@/features/mentions/registry';
import { MentionChip } from '@/features/mentions/components/MentionChip';
import { searchContacts } from '@/infra/api/client';
import { navigate } from '@/infra/navigation/history';
import { store } from '@/infra/store';
import { enableContactAction } from './ducks';
import { selectContacts } from './selectors';
import { Contact } from '@/infra/types/domain';

registerMentionProvider({
  kind: 'contact',
  trigger: '@',
  async search(q) {
    const query = q.trim();
    if (!query) return [];
    const results = await searchContacts(query);
    // lifedb search matches on handles too (emails, phones). Filter those out
    // client-side — a mention dropdown triggered by "@sc" shouldn't surface
    // every contact whose email contains "sc". We only keep rows where the
    // query matches against a name token.
    const needle = query.toLowerCase();
    const nameMatches = results.filter((c) => {
      const haystacks = [c.display_name, c.first_name, c.last_name]
        .filter((s): s is string => Boolean(s))
        .map((s) => s.toLowerCase());
      return haystacks.some((h) => h.includes(needle));
    });
    return nameMatches.slice(0, 8).map((c) => ({
      id: String(c.id),
      label: c.display_name,
      hint: c.role || c.organization || undefined,
      avatarUrl: c.photo_url || undefined,
    }));
  },
  renderChip({ id, label }) {
    return <MentionChip id={id} label={label} kind="contact" />;
  },
  onChipClick(id) {
    // Always route to the Contacts page — that page owns the modal reducer
    // state (ContactDetailModal lives inside ContactsPage) and auto-fetches
    // the enabled list on mount.
    navigate(`/contacts?contact=${id}`);
  },
  onInsert(match) {
    // Auto-enable any mentioned contact that isn't already enabled. Without
    // this, clicking the chip later would open /contacts?contact=:id and find
    // nothing — selectContacts only holds enabled rows.
    // contacts is a plugin-loaded reducer, so store's static state type omits
    // it. Cast to `any` and let the selector read `state.contacts`.
    const state = store.getState() as any;
    const contacts = selectContacts(state) as Contact[];
    const id = match.id;
    if (!id) return;
    if (contacts.some((c) => c.id === id)) return;
    store.dispatch(enableContactAction(id));
  },
});
